import { Ref, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCoreStore } from 'src/stores/core/store';
import { AppCoreEnums } from 'src/core/shared/enums';
import { IDateRange, IDynamicFilterItem, TableBasedContainerData } from 'src/stores/core/models';

export default () => {
  const {
    collapsibleTableContainerData,
    tableContainerData,
    tableInnerContainerData,
    collapsibleContainerData,
    listContainerData
  } = storeToRefs(useCoreStore());

  const resetContainerDynamicFilters = (containerType: AppCoreEnums.ContainerType, keepSticky = false) => {
    const containerData = getTableBasedContainerData(containerType);
    const stickyDynamicFilters = keepSticky ? containerData.value?.dynamicFilters.filter((x) => !!x.isStickyInternal) : [];
    containerData.value.dynamicFilters = [...stickyDynamicFilters];
  };

  const getTableBasedContainerData = (containerType: AppCoreEnums.ContainerType) => {
    switch (containerType) {
      case AppCoreEnums.ContainerType.CollapsibleTableContainer:
        return collapsibleTableContainerData;

      case AppCoreEnums.ContainerType.TableContainer:
        return tableContainerData;

      case AppCoreEnums.ContainerType.TableInnerContainer:
        return tableInnerContainerData;

      case AppCoreEnums.ContainerType.CollapsibleContainer:
        return collapsibleContainerData;

      case AppCoreEnums.ContainerType.ListContainer:
        return listContainerData;
    }

    return ref(new TableBasedContainerData());
  };

  const addContainerDynamicFilter = (
    containerType: AppCoreEnums.ContainerType,
    filterItem: IDynamicFilterItem,
    mergeExistingValues = false
  ) => {
    const containerData = getTableBasedContainerData(containerType);
    const index = containerData.value.dynamicFilters.findIndex((x) => x.key === filterItem.key);
    let filterItemValues = '' as string | string[] | IDateRange | number | undefined;

    if (index >= 0) {
      // There is already a filter item. we need to add or merge filter item options.
      if (mergeExistingValues) {
        // merge
        const existingFilterItem = containerData.value.dynamicFilters[index];

        if (filterItem.type === AppCoreEnums.FilterItemType.MultiSelect) {
          (filterItem.value as string[]).forEach((x) => (existingFilterItem.value as string[]).push(x));
        } else {
          existingFilterItem.value = filterItem.value;
        }

        filterItemValues = existingFilterItem.value;
      } else {
        // replace
        containerData.value.dynamicFilters.splice(index, 1, filterItem);
        filterItemValues = filterItem.value;
      }
    } else {
      // add
      containerData.value.dynamicFilters.push(filterItem);
      filterItemValues = filterItem.value;
    }

    return filterItemValues;
  };

  const removeContainerDynamicFilter = (
    containerType: AppCoreEnums.ContainerType,
    filterItem: IDynamicFilterItem,
    mergeExistingValues = false
  ) => {
    const containerData = getTableBasedContainerData(containerType);
    const index = containerData.value.dynamicFilters.findIndex((x) => x.key === filterItem.key);
    let remainingFilterItemValues = [] as string | string[];

    if (index >= 0) {
      if (mergeExistingValues) {
        const existingFilterItem = containerData.value.dynamicFilters[index];

        if (filterItem.type === AppCoreEnums.FilterItemType.MultiSelect) {
          (filterItem.value as string[]).forEach((x) => {
            const valueIndex = (existingFilterItem.value as string[]).findIndex((y) => y === x);
            if (valueIndex >= 0) {
              (existingFilterItem.value as string[]).splice(valueIndex, 1);
            }
          });
          remainingFilterItemValues = existingFilterItem.value as string[];
        } else {
          containerData.value.dynamicFilters.splice(index, 1);
        }
      } else {
        containerData.value.dynamicFilters.splice(index, 1);
      }
    }

    return remainingFilterItemValues;
  };

  return {
    getTableBasedContainerData,
    addContainerDynamicFilter,
    removeContainerDynamicFilter,
    resetContainerDynamicFilters,
  };
};
